import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';

const Calculator = () => {
  const { state } = useParams();
  const [assessmentValue, setAssessmentValue] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stateAbbreviationMap, setStateAbbreviationMap] = useState({});

  // Check if this is the national average calculator
  const isNational = state === 'national';

  // Convert state abbreviation to full name
  const getFullStateName = (abbr) => {
    // Special handling for DC
    if (abbr === 'DC') {
      return 'Washington DC';
    }
    return stateAbbreviationMap[abbr] || null;
  };

  // Get display name for the current state (either full name or abbr)
  const stateDisplayName = isNational ? 
    'National Average' : 
    (state ? (getFullStateName(state) || state) : '');

  // SEO title and description
  const property_tax_calculator_title = isNational ? 
    'National Property Tax Calculator | Calculate Average Property Tax Rates' : 
    `${stateDisplayName} Property Tax Calculator | Calculate Your Property Taxes`;
  
  const property_tax_calculator_description = isNational ?
    'Calculate the average property tax across all 50 states and D.C. Compare your property tax rates with the national average.' :
    `Calculate your property taxes in ${stateDisplayName}. Get accurate property tax estimates based on your assessment value.`;

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get('/api/state_abbreviations/');
        // Convert the array of states to a map for easy lookup
        const stateMap = {};
        response.data.states.forEach(s => {
          stateMap[s.abbr] = s.name;
        });
        setStateAbbreviationMap(stateMap);
      } catch (error) {
        console.error('Error fetching states:', error);
      }
    };

    fetchStates();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // For national average, pass null as state
      // For states, convert state abbr to full name for API
      const fullStateName = isNational ? null : (state ? getFullStateName(state) : null);
      
      console.log('State abbreviation:', state);
      console.log('Full state name:', fullStateName);
      
      const response = await axios.get('/api/calculate_property_tax/', {
        params: {
          state: fullStateName,
          assessment_value: assessmentValue,
        }
      });
      setResult(response.data);
    } catch (err) {
      setError('Failed to calculate property tax. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  return (
    <>
      <Helmet>
        <title>{property_tax_calculator_title}</title>
        <meta name="description" content={property_tax_calculator_description} />
        <meta name="keywords" content={`property tax calculator, ${stateDisplayName} property tax, real estate taxes, property assessment, tax rate calculator`} />
        <meta property="og:title" content={property_tax_calculator_title} />
        <meta property="og:description" content={property_tax_calculator_description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={property_tax_calculator_title} />
        <meta name="twitter:description" content={property_tax_calculator_description} />
      </Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Property Tax Calculator {stateDisplayName ? `for ${stateDisplayName}` : ''}
          </Typography>
          
          {isNational && (
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              Calculate the average property tax across all 50 states and D.C. to see how your area compares to the national average.
            </Typography>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Property Assessment Value"
              variant="outlined"
              type="number"
              value={assessmentValue}
              onChange={(e) => setAssessmentValue(e.target.value)}
              InputProps={{
                startAdornment: <Typography>$</Typography>,
              }}
              required
              sx={{ mb: 3 }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || !assessmentValue}
            >
              {loading ? <CircularProgress size={24} /> : 'Calculate Property Tax'}
            </Button>
          </Box>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {result && (
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Results for {result.state}:
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Tax Rate
                    </Typography>
                    <Typography variant="h6">{result.tax_rate}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Annual Tax
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(result.annual_tax)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Monthly Tax
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(result.monthly_tax)}
                    </Typography>
                  </Paper>
                </Grid>
                {result.rank && (
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      {result.state} ranks #{result.rank} in property tax rates among all states.
                    </Typography>
                  </Grid>
                )}
                {isNational && (
                  <Grid item xs={12}>
                    <Paper elevation={1} sx={{ p: 3, mt: 2, bgcolor: 'rgba(63, 81, 181, 0.05)', borderLeft: '4px solid #3f51b5' }}>
                      <Typography variant="body1">
                        The national average property tax rate is calculated based on the effective tax rates across all 50 states and D.C. 
                        Your property tax may be higher or lower depending on your location. Use our state-specific calculators to see 
                        how your state compares to the national average.
                      </Typography>
                      <Button 
                        component={Link} 
                        to="/property_tax_calculator" 
                        variant="outlined" 
                        color="primary"
                        sx={{ mt: 2 }}
                      >
                        See All State Calculators
                      </Button>
                    </Paper>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3, mt: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderLeft: '4px solid #1976d2' }}>
                    <Typography variant="h6" gutterBottom>
                      Want to see how your property compares?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Get a personalized analysis of your property's value and potential tax savings. 
                      Our comprehensive property evaluation will help you understand your home's true worth 
                      and how it stacks up against similar properties in your area.
                    </Typography>
                    <Button 
                      component={Link} 
                      to="/" 
                      variant="contained" 
                      color="primary"
                      sx={{ mt: 2 }}
                    >
                      Check Your Property
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default Calculator; 